import { useRef, useState, type FormEvent } from "react";
import { Icon, type IconName } from "../components/Icon";
import { sendMagicLink, type SignInProblem } from "../lib/auth";
import { BRAND, hasModule, logoWidth, siteLink } from "../lib/config";
import { fieldSignIn } from "../modules/field";

/** The note under the sign-in form: the client's own (portal.signInNote), a module's, or a neutral default. */
function signInNote(): { note: string; icon: IconName | null } {
  const chosen = (i: string | null): IconName | null => (i === "none" ? null : ((i ?? "lock") as IconName));
  if (BRAND.signInNote) return { note: BRAND.signInNote, icon: chosen(BRAND.signInIcon) };
  const field = fieldSignIn(hasModule);
  if (field) return { note: field.note, icon: BRAND.signInIcon ? chosen(BRAND.signInIcon) : field.icon };
  return { note: "**Staff only.** Ask a director to invite you.", icon: chosen(BRAND.signInIcon) };
}

/** "**bold** rest" to React nodes (sign-in note only; no other markup). */
function boldText(s: string) {
  return s.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**") ? <strong key={i}>{part.slice(2, -2)}</strong> : part,
  );
}

type State = { kind: "idle" } | { kind: "sending" } | { kind: "sent"; email: string } | { kind: "error"; message: string };

/** What to tell staff when a sign-in link is refused. */
function friendly({ message, problem }: { message: string; problem: SignInProblem }): string {
  switch (problem) {
    case "not_invited":
      return "That email isn't set up for the portal. Ask a director to send you an invite, then try again.";
    case "invite_not_accepted":
      return "You've been invited, but the invitation hasn't been accepted yet. Open the invitation email and accept it, then sign in here. Can't find it? Ask a director to send it again.";
    case "not_invited_or_pending":
      return "That email isn't set up for the portal yet, or its invitation hasn't been accepted. Accept your invitation email, or ask a director to invite you.";
    case "rate_limited":
      return "Too many sign-in emails were requested. Wait a minute, then try again.";
    default:
      return message;
  }
}

/** Magic link sign-in. Accounts are invited by a director; sign-up is disabled. */
export function Login() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState<State>({ kind: "idle" });
  const input = useRef<HTMLInputElement>(null);
  const note = signInNote();

  async function submit(e: FormEvent) {
    e.preventDefault();
    setState({ kind: "sending" });
    const { error } = await sendMagicLink(email.trim());
    setState(error ? { kind: "error", message: friendly(error) } : { kind: "sent", email: email.trim() });
  }

  function reset() {
    setState({ kind: "idle" });
    requestAnimationFrame(() => input.current?.focus());
  }

  return (
    <div className="login">
      <div className="login__brand">
        <div className="login__brand-inner">
          <img
            src={BRAND.logo}
            srcSet={`${BRAND.logoSmall} 320w, ${BRAND.logo} 640w`}
            sizes="(max-width: 719px) 150px, 300px"
            width={logoWidth(503)}
            height="503"
            alt={BRAND.name}
            className="login__logo"
          />
          <div className="login__copy">
            <p className="eyebrow eyebrow--accent">Staff portal</p>
            <p className="login__headline">{BRAND.headline}</p>
            <p className="login__tagline">{BRAND.tagline}</p>
          </div>
        </div>
        {BRAND.style === "bold" && <div className="brand-stripe" aria-hidden="true" />}
      </div>

      <main className="login__panel">
        <div className="login__form-wrap">
          {state.kind === "sent" ? (
            <div className="login__sent" role="status">
              <span className="login__sent-icon" aria-hidden="true"><Icon name="mail" size={28} /></span>
              <h1>Check your email</h1>
              <p>
                We sent a sign-in link to <strong>{state.email}</strong>. Open it on this device to sign in. It can take a
                minute to arrive, so check your junk folder too.
              </p>
              <button type="button" className="btn btn--outline btn--block" onClick={reset}>Use a different email</button>
            </div>
          ) : (
            <form className="login__form" onSubmit={submit}>
              <p className="eyebrow">{BRAND.name}</p>
              <h1>Sign in</h1>
              <p className="login__intro">Enter your work email and we'll send you a one-time sign-in link. No password needed.</p>
              <div className="field">
                <label htmlFor="email" className="field__label">Work email</label>
                <input
                  ref={input}
                  id="email"
                  className="input input--lg"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  aria-invalid={state.kind === "error" ? true : undefined}
                  aria-describedby={state.kind === "error" ? "login-error" : undefined}
                />
              </div>
              {state.kind === "error" && (
                <p id="login-error" className="login__error" role="alert">
                  <Icon name="warning" size={20} /> <span>{state.message}</span>
                </p>
              )}
              <button type="submit" className="btn btn--primary btn--block btn--lg" disabled={state.kind === "sending"} aria-busy={state.kind === "sending"}>
                {state.kind === "sending" ? "Sending link" : "Email me a sign-in link"}
              </button>
            </form>
          )}

          <div className="login__note">
            {note.icon && <Icon name={note.icon} size={22} />}
            <p>
              {boldText(note.note)}
              {BRAND.loginGateway && <>{" "}<a href={siteLink("/login/")}>See all logins</a></>}
            </p>
          </div>
        </div>
      </main>
    </div>
  );
}
